import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Helmet } from 'react-helmet';
import { Link, NavLink, useHistory } from 'react-router-dom';
import TitleStatusButton from '../../components/TitleStatusButton';
import Loading from '../../components/Loading';
import { ReactComponent as ChevronLeft } from '../../svg/chevron-left.svg';
import Layout from '../Layout';
import TitleMetadata from '../../pages/title/Metadata.jsx';
import TitleAssets from '../../pages/title/Assets.jsx';
import ReviewSubmit from './ReviewSubmit';
import {
    titleLoading,
    titleLoadingFailed,
    titleUpdate,
    getTitleById,
    titleReset,
    takedownTitleById
} from '../../slices/titleSlice';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../components/Modal';
import { toast } from 'react-toastify';
import GiveawayCodes from './GivawayCodes';

const TitleWrapper = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const session = useSelector((state) => state.session);
    const [takedownModalActive, setTakedownModalActive] = useState(false);
    const [requestingTakedown, setRequestingTakedown] = useState(false);
    const { data, isLoading, isError } = useSelector((state) => state.title);

    useEffect(() => {
        (async () => {
            try {
                dispatch(titleLoading());
                let title = await getTitleById(props.match.params.catId);
                dispatch(titleUpdate(title));
            } catch {
                dispatch(titleLoadingFailed());
            }
        })();
        return () => {
            dispatch(titleReset());
        };
    }, [props.match.params.catId, dispatch]);

    let canEdit = !session.isReadOnly && session.enableEditing;
    const pathSegments = props.location.pathname.split('/');
    const page = pathSegments[pathSegments.length - 1]?.toLowerCase();

    const takedownRequest = async () => {
        try {
            setRequestingTakedown(true);
            await takedownTitleById(props.match.params.catId);
            history.push('/catalog');
            toast.success(`Takedown Requested for: ${props.match.params.catId}`);
        } catch {
            setRequestingTakedown(false);
            toast.error('Error Requesting Takedown');
        }
    };

    return (
        <>
            {page === 'submit' && <ReviewSubmit />}
            {page !== 'submit' && (
                <Layout>
                    {isLoading && <Loading />}
                    {!isLoading && isError && <div className="alert alert-danger">Not Found</div>}
                    {!isLoading && data && (
                        <div>
                            <Helmet>
                                <title>{data.title}</title>
                            </Helmet>
                            <p>
                                <Link className="alt-link" to="/catalog">
                                    <ChevronLeft width="1em" height="1em" preserveAspectRatio="xMidYMid meet" />
                                    Back to Catalog
                                </Link>
                            </p>
                            <div className="page-header">
                                <h1>
                                    {data.title}
                                    {data.subtitle && <small className="text-muted">: {data.subtitle}</small>}
                                    <span className="ml-2">
                                        <TitleStatusButton status={data.status} />
                                    </span>
                                </h1>
                            </div>
                            <ul className="title-nav">
                                <li className="nav-item">
                                    <NavLink
                                        className={classnames('nav-link', { active: page === 'metadata' })}
                                        to={`/catalog/${props.match.params.catId}/metadata`}
                                    >
                                        Metadata
                                    </NavLink>
                                </li>
                                <li className="nav-item">
                                    <NavLink
                                        className={classnames('nav-link', { active: page === 'assets' })}
                                        to={`/catalog/${props.match.params.catId}/assets`}
                                    >
                                        Audio
                                    </NavLink>
                                </li>
                                {session.isAdmin && (
                                    <li className="nav-item">
                                        <NavLink
                                            className={classnames('nav-link', { active: page === 'codes' })}
                                            to={`/catalog/${props.match.params.catId}/codes`}
                                        >
                                            Codes
                                        </NavLink>
                                    </li>
                                )}
                                {canEdit && (
                                    <li className="nav-item nav-cta">
                                        <button
                                            className="btn btn-danger btn-small"
                                            onClick={() => setTakedownModalActive(!takedownModalActive)}
                                        >
                                            Request Takedown
                                        </button>

                                        <Link
                                            className="btn btn-primary"
                                            to={`/catalog/${props.match.params.catId}/submit`}
                                        >
                                            Review & Submit
                                        </Link>
                                    </li>
                                )}
                            </ul>
                            <div>
                                {page === 'assets' && <TitleAssets {...props} />}
                                {page === 'metadata' && <TitleMetadata {...props} />}
                                {page === 'codes' && <GiveawayCodes {...props} />}
                            </div>
                        </div>
                    )}
                    <Modal
                        label="Takedown Request"
                        visible={takedownModalActive}
                        onClose={() => setTakedownModalActive(false)}
                        innerClassName="modal-s text-center"
                    >
                        <div>
                            <h2>Confirm Takedown Request</h2>
                            <p>
                                A takedown request will have this title removed from all retailer catalogs, preventing
                                future sales. Listeners will retain access to any previously purchased content
                            </p>
                            <p>
                                Takedowns can take up to 24 hours to be reflected in Passport, and up to 30 days to be
                                implemented by all retailers.
                            </p>
                            <p>Please confirm your takedown request.</p>
                            <div>
                                <button
                                    className="btn btn-small"
                                    disabled={requestingTakedown}
                                    onClick={() => setTakedownModalActive(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger btn-small"
                                    disabled={requestingTakedown}
                                    onClick={takedownRequest}
                                >
                                    Confirm Takedown
                                </button>
                            </div>
                        </div>
                    </Modal>
                </Layout>
            )}
        </>
    );
};

export default TitleWrapper;
